<script lang="ts" setup>
import type { State } from '@autobid/nuxt-pinia-store/types/Search.js'
import type { RestComponent } from '@autobid/strapi-integration/utils/content'
import { extractStrapiComponents } from '@autobid/strapi-integration/utils/content'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useCarPageType } from '@autobid/ui/composables/car/useCarPageInfo'
import { usePdfQuery } from '@autobid/ui/composables/usePdfQuery'
import { useQuery } from '@tanstack/vue-query'
import { onBeforeUnmount, onServerPrefetch, provide, ref } from 'vue'

const pageType = useCarPageType()
const { pdfQueryParams, shouldGenerate } = usePdfQuery()
const pdfDebounce = ref<NodeJS.Timeout>()
const isReady = ref(false)
const { locale } = useI18n()
const { $trpc } = useNuxtApp()
provide('isPdfPage', true)
const { generated, pdfHeader, pdfContent, pdfFooter } = toRefs(
  reactive<State>({
    headerComponents: [],
    contentComponents: [],
    footerComponents: [],
  }),
)

const componentNames = {
  ComponentLayoutSimpleHeader: 'CustomLayoutSimplePdfHeader',
  ComponentLayoutSimpleFooter: 'CustomLayoutSimplePdfFooter',
  ...(pageType.value === 'detailsPreview' ? { ComponentDynamicAuctionItemDetails: 'DynamicAuctionItemDetailsPreview' } : {}),
}

const { data, suspense } = useQuery(
  ['pdf', pdfQueryParams.value.pdfPageSlug, locale.value],
  async () => {
    try {
      const data = await $trpc.cms.getPdfPage.query({
        slug: String(pdfQueryParams.value.pdfPageSlug),
        lang: locale.value,
      })

      return {
        headerComponents: extractStrapiComponents(
          data?.headerSections as RestComponent[],
        ),
        contentComponents: extractStrapiComponents(
          data?.contentSections as RestComponent[],
        ),
        footerComponents: extractStrapiComponents(
          data?.footerSections as RestComponent[],
        ),
      }
    }
    catch (error) {
      const { handled, message, code } = getError(error)

      if (!handled) {
        showError({ statusCode: code, statusMessage: message })
      }
    }
  },
  {
    refetchOnWindowFocus: false,
  },
)
onServerPrefetch(suspense)

const VALIDATION_MAX_RETRIES = 5
const leftValidationTries = ref(VALIDATION_MAX_RETRIES)
function validateAmountOfRenderedComponents() {
  if (!pdfContent.value || !pdfHeader.value || !pdfFooter.value) {
    return false
  }

  const isHeaderValid
    = data.value?.headerComponents.length
    // @ts-expect-error todo: fix types
      === pdfHeader.value.children[0].childElementCount
  const isContentValid
    = data.value?.contentComponents.length
    // @ts-expect-error todo: fix types
      === pdfContent.value.children[0].childElementCount
  const isFooterValid
    = data.value?.footerComponents.length
    // @ts-expect-error todo: fix types
      === pdfFooter.value.children[0].childElementCount

  return isHeaderValid && isContentValid && isFooterValid
}

async function createPdfDebounce() {
  const areAllComponentsRendered = validateAmountOfRenderedComponents()

  if (!areAllComponentsRendered) {
    leftValidationTries.value--

    if (leftValidationTries.value) {
      sendRequest()
    }
    else {
      // todo: handle errors differently perhaps notify pdf service
    }

    return
  }
  isReady.value = true
}

function sendRequest() {
  clearTimeout(pdfDebounce.value)

  if (generated.value || !data.value)
    return

  pdfDebounce.value = setTimeout(createPdfDebounce, 300)
}

onBeforeUnmount(() => {
  clearTimeout(pdfDebounce.value)
})

watch([data, pdfQueryParams, shouldGenerate], () => {
  leftValidationTries.value = VALIDATION_MAX_RETRIES
  sendRequest()
})
</script>

<template>
  <div class="h-full min-h-[100vh] w-full">
    <div v-if="isReady" class="sr-only">
      is-ready
    </div>
    <div v-if="data && shouldGenerate" class="container mx-auto">
      <div id="pdf-header" ref="pdfHeader">
        <AutobidRenderComponents
          :components="data.headerComponents"
          :component-names="componentNames"
        />
      </div>
      <div id="pdf-content" ref="pdfContent">
        <AutobidRenderComponents
          :components="data.contentComponents"
          :component-names="componentNames"
        />
      </div>
      <div id="pdf-footer" ref="pdfFooter">
        <AutobidRenderComponents
          class="w-full"
          :components="data.footerComponents"
          :component-names="componentNames"
        />
      </div>
    </div>
  </div>
</template>
